/**
 * Keeping numbers, times and dates the right way round in Arabic text.
 *
 * THE BUG THIS FIXES (QA report §1.3)
 *
 * In a right-to-left paragraph, a run like "09:00 — 17:00" is not one unit to
 * the Unicode bidi algorithm: it is a number, a neutral dash, and a number, and
 * rule N1 resolves the dash between two European numbers as right-to-left. The
 * browser then lays the two numbers out right to left — "17:00 — 09:00" — and a
 * clinic that opens at nine appears to open at five. The same happens to date
 * ranges, "10:30 - 11:00" arrival windows, and "3/12" fractions.
 *
 * The fix is an isolate: U+2066 LEFT-TO-RIGHT ISOLATE … U+2069 POP DIRECTIONAL
 * ISOLATE. Inside it the run is laid out left to right as one unit, and to the
 * Arabic around it the whole thing is a single neutral object, so nothing
 * outside it moves either. It is a character, not markup, so it works in
 * plain-string props (hints, titles, toasts) as well as in JSX.
 *
 * DATES (QA report §2.3)
 *
 * The report asks for day/month/year. `formatDmy` renders it; `parseDmy`
 * reads it back, accepting the Arabic-Indic digits a phone keyboard types and
 * any of / - . as the separator.
 */

export const LRI = '⁦';
export const PDI = '⁩';

/** Wrap a run so it is laid out left to right, as one unit. */
export function ltr(value) {
  if (value === null || value === undefined || value === '') return '';
  return `${LRI}${value}${PDI}`;
}

/** "09:00 – 17:00", the right way round in any paragraph. Either end may be empty. */
export function timeRange(from, to, separator = '–') {
  if (!from && !to) return '';
  if (!to) return ltr(from);
  if (!from) return ltr(to);
  return ltr(`${from} ${separator} ${to}`);
}

const ARABIC_INDIC = '٠١٢٣٤٥٦٧٨٩';
const PERSIAN = '۰۱۲۳۴۵۶۷۸۹';

/** "٢٥/٠٩/٢٠٢٦" → "25/09/2026". Anything that is not a digit is left alone. */
export function toAsciiDigits(value) {
  return String(value ?? '').replace(/[٠-٩۰-۹]/g, (d) => {
    const i = ARABIC_INDIC.indexOf(d);
    return String(i >= 0 ? i : PERSIAN.indexOf(d));
  });
}

const pad = (n) => String(n).padStart(2, '0');

/** Is this a real calendar date? 2026-02-30 is not. */
export function isValidYmd(y, m, d) {
  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return false;
  if (y < 1000 || y > 9999 || m < 1 || m > 12 || d < 1) return false;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

/** "2026-09-25" (or a Date, or an ISO timestamp) → "25/09/2026". Empty for nothing. */
export function formatDmy(value) {
  if (!value) return '';
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return '';
    return `${pad(value.getDate())}/${pad(value.getMonth() + 1)}/${value.getFullYear()}`;
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (!m) return '';
  return `${m[3]}/${m[2]}/${m[1]}`;
}

/**
 * "25/9/2026", "٢٥-٠٩-٢٠٢٦", "25.09.2026" → "2026-09-25". Null when it is not a
 * complete, real date. Day first, always — the order the report asks for.
 */
export function parseDmy(text) {
  const clean = toAsciiDigits(text).trim();
  const m = /^(\d{1,2})[/.\-\s](\d{1,2})[/.\-\s](\d{4})$/.exec(clean);
  if (!m) return null;
  const d = Number(m[1]);
  const mo = Number(m[2]);
  const y = Number(m[3]);
  if (!isValidYmd(y, mo, d)) return null;
  return `${y}-${pad(mo)}-${pad(d)}`;
}

/**
 * Reformat what is being typed into dd/mm/yyyy as the digits arrive: the
 * slashes appear on their own, and a pasted "25092026" becomes "25/09/2026".
 */
export function maskDmy(raw) {
  const digits = toAsciiDigits(raw).replace(/\D/g, '').slice(0, 8);
  const parts = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)].filter(Boolean);
  return parts.join('/');
}

/** "0930", "9:30", "٩:٣٠" → "09:30"; null when it is not a time of day. */
export function parseTime(text) {
  const clean = toAsciiDigits(text).trim();
  let m = /^(\d{1,2}):(\d{2})$/.exec(clean);
  if (!m) m = /^(\d{2})(\d{2})$/.exec(clean);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return `${pad(h)}:${pad(min)}`;
}

/** Reformat what is typed into HH:MM as the digits arrive. */
export function maskTime(raw) {
  const digits = toAsciiDigits(raw).replace(/\D/g, '').slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
}

/** "13:30" → "01:30 م" — the twelve-hour reading shown beside a 24-hour field. */
export function twelveHour(hhmm) {
  const m = /^(\d{2}):(\d{2})$/.exec(String(hhmm || ''));
  if (!m) return '';
  const h = Number(m[1]);
  const suffix = h < 12 ? 'ص' : 'م';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${pad(h12)}:${m[2]} ${suffix}`;
}

export const ARABIC_MONTHS = Object.freeze([
  'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
  'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر',
]);

/** Saturday first, the way the week is read here. */
export const ARABIC_WEEKDAYS_SAT_FIRST = Object.freeze(['سبت', 'أحد', 'إثنين', 'ثلاثاء', 'أربعاء', 'خميس', 'جمعة']);
